import { useState } from "react";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { PageHeader } from "@/components/CrudModule";
import { CheckCircle2, Circle, Rocket } from "lucide-react";

const CHECKLISTS = {
  admin: [
    "Review the Production-Readiness checklist in Settings",
    "Review Pilot Mode in Settings (off by default) before handling sensitive client data",
    "Create real team users (Team → Add User) and send invite links",
    "Configure reference data (carriers, products, states, sources) in Settings",
    "Run a full data export and verify a backup is recorded",
    "Set the production CORS origin and disable demo data before launch",
  ],
  ops_manager: [
    "Review the Lead Pipeline and assign owners",
    "Confirm carrier & vendor scorecard ratings are current",
    "Check the Commissions ledger for missing entries",
    "Set up your daily Saved Views on the Dashboard",
    "Review Pending / Requote queue and follow-up tasks",
  ],
  agent: [
    "Open 'Assigned to Me' on the Dashboard to see your leads",
    "Work New Leads through Contacted → Fact Find → Ready to Quote",
    "Prepare quotes matched to client budget in Quotes & Applications",
    "Submit applications and track carrier decisions",
    "Keep follow-up tasks current for issued policies",
  ],
  bpo: [
    "Use Intake Queue to add new leads with full fact-find",
    "Mark health intake complete before handoff",
    "Set the correct lead source and lead cost",
    "Add clear notes for the licensed agent",
    "Set handoff status to 'Handed Off to Agent' when ready",
  ],
  future: [
    "Review leads assigned to you on the Dashboard",
    "Shadow assigned tasks and follow-up activity",
    "Ask your manager about quoting workflows",
  ],
};

export default function Onboarding() {
  const { user } = useAuth();
  const items = CHECKLISTS[user.role] || [];
  const storeKey = `foa_onboarding_${user.id}`;
  const [done, setDone] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storeKey)) || {}; } catch { return {}; }
  });

  const toggle = (i) => {
    const next = { ...done, [i]: !done[i] };
    setDone(next);
    localStorage.setItem(storeKey, JSON.stringify(next));
  };

  const completed = items.filter((_, i) => done[i]).length;

  return (
    <div>
      <PageHeader title="Onboarding" subtitle={`Getting-started checklist for ${ROLE_LABELS[user.role]}.`} />
      <div className="max-w-2xl">
        <div className="mb-4 flex items-center gap-3 rounded-lg border border-border bg-card p-4">
          <div className="grid h-10 w-10 place-items-center rounded-md bg-primary/10 text-primary"><Rocket className="h-5 w-5" /></div>
          <div className="flex-1">
            <div className="text-sm font-medium">{completed} of {items.length} steps complete</div>
            <div className="mt-1.5 h-2 w-full rounded-full bg-secondary">
              <div className="h-2 rounded-full bg-primary transition-all" style={{ width: `${items.length ? (completed / items.length) * 100 : 0}%` }} />
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-border bg-card divide-y divide-border">
          {items.map((it, i) => (
            <button key={i} onClick={() => toggle(i)} data-testid={`onboarding-item-${i}`}
              className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm hover:bg-secondary transition-colors">
              {done[i] ? <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" /> : <Circle className="h-5 w-5 text-muted-foreground shrink-0" />}
              <span className={done[i] ? "line-through text-muted-foreground" : ""}>{it}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
